import { ReactElement } from 'react';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import {
  Alert,
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  ParametersType,
  ParticleData,
  PriorityList
} from '../constants/telosair-interfaces';
import { getPriorityColor } from '../_helpers/dashboardHelper';
import { getDisplayableValue, getParameterName } from '../_helpers/globalHelper';
import { resolveStatus } from '../_helpers/airQuality';
import { useUnifiedData } from '../hooks/useUnifiedData';

type DeviceSelectState = {
  x: number;
  y: number;
  deviceData: DeviceAttributesV1;
  particleData: ParticleData;
  alert: Alert[];
};

type MapDeviceMarkerProps = {
  deviceData: DeviceAttributesV1;
  selectedDevice?: number | null;
  /**
   * Parameter the badge should display. When omitted (the legend's "All"
   * selection) the marker falls back to AQI, or the first parameter by
   * display order when AQI is not available.
   */
  displayParameter?: ParametersType | null;
  onSelect: (state: DeviceSelectState) => void;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

/**
 * A clean, professional device marker for the full-screen map. Renders a
 * status-coloured badge with the device's primary reading and reveals the
 * device name on hover. Clicking opens the device detail panel.
 * @param props - Props for the marker.
 * @returns A Google Maps advanced-marker element.
 */
export default function MapDeviceMarker(props: MapDeviceMarkerProps): ReactElement {
  const { deviceData, selectedDevice, displayParameter, onSelect, setDataFilter } = props;
  const { parameters, deviceDataSummary, clientThresholdSettings, userConfigDataV2, devices } =
    useUnifiedData();

  const particleData: ParticleData = deviceDataSummary[deviceData.serial_number]?.parameters || {};
  const deviceType =
    devices.find((d) => d.serialNumber === deviceData.serial_number)?.deviceType ||
    DeviceType.MIXED;

  // The badge follows the parameter picked in the map legend. With no
  // selection ("All") it prefers AQI, otherwise the first parameter by
  // display order.
  const sorted = [...(parameters || [])].sort((a, b) => a.order - b.order);
  const primary =
    (displayParameter ? sorted.find((p) => p.parameterName === displayParameter) : null) ||
    sorted.find((p) => p.parameterName === ('aqi' as ParametersType)) ||
    sorted[0] ||
    null;

  const reading = primary ? particleData[primary.parameterName] : null;
  // -9999 is the backend's "no reading" sentinel and must not be plotted.
  // Readings arrive already expressed in the user's selected unit.
  const value = reading === -9999 || reading === undefined ? null : reading;

  const priority = primary
    ? getPriorityColor(
        primary.parameterName,
        value,
        clientThresholdSettings,
        userConfigDataV2,
        deviceType
      )
    : PriorityList.none;

  const status = primary
    ? resolveStatus(primary.parameterName, value, priority)
    : { label: 'No data', badgeClass: 'bg-gray-500' };
  const valueText = value !== null && primary ? getDisplayableValue(primary.parameterName, value) : '--';
  const isSelected = selectedDevice === deviceData.serial_number;

  return (
    <AdvancedMarker
      position={{ lat: deviceData.y, lng: deviceData.x }}
      title={deviceData.deviceName}
      zIndex={isSelected ? 50 : undefined}
      onClick={() => {
        setDataFilter?.({
          type: 'device',
          id: deviceData.serial_number,
          name: deviceData.deviceName
        });
        onSelect({ x: 0, y: 0, deviceData, particleData, alert: [] });
      }}
    >
      <div className="group relative flex -translate-y-1/2 flex-col items-center">
        <div
          className={`flex h-8 min-w-[2rem] items-center justify-center rounded-full border-2 px-1.5 text-xs font-bold text-white shadow-md transition-transform group-hover:scale-110 ${
            status.badgeClass
          } ${isSelected ? 'border-gray-900 ring-2 ring-white' : 'border-white'}`}
        >
          {valueText}
        </div>
        <span className="pointer-events-none absolute top-9 whitespace-nowrap rounded bg-gray-900/90 px-2 py-0.5 text-[10px] font-medium text-white opacity-0 shadow transition-opacity group-hover:opacity-100">
          {deviceData.deviceName}
          {primary ? ` · ${getParameterName(primary, deviceType).shortName}` : ''}
        </span>
      </div>
    </AdvancedMarker>
  );
}
